import {
  Form,
  Input,
  Drawer,
  Select,
  Button,
  Row,
  Col,
  Space,
  Divider,
  message,
  Checkbox,
  TimePicker,
  Upload,
  Modal,
  Grid,
  Tooltip,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useChargerCreateAndUpdate, useGetSingleCharger } from '../Hooks';
import { useOperatorStore } from '../../../store/useOperatorStore';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { parse } from 'date-fns';
import { useFilUpload } from '../../../Hooks/Index';
import { BsPlus } from 'react-icons/bs';
// useWatch imported from Form.useWatch
import { validations } from '../../../utils/validation';
import PropTypes from 'prop-types';
import { Typography } from 'antd';
const { Title } = Typography;
const { useBreakpoint } = Grid;

// Constants
const MAX_CONNECTORS = 3;


const parseTime = (timeString) =>
  parse(timeString, 'HH:mm', new Date(1970, 0, 1));

// eslint-disable-next-line react-refresh/only-export-components
export const defaultOpeningTimes = [
  {
    day: 'Monday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
  {
    day: 'Tuesday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
  {
    day: 'Wednesday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
  {
    day: 'Thursday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
  {
    day: 'Friday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
  {
    day: 'Saturday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
  {
    day: 'Sunday',
    timeRange: [parseTime('09:00'), parseTime('22:00')],
  },
];

const CreateCharger = ({
  isOpen,
  onOpenChange,
  id,
  locationId,
  passOperatorId,
}) => {
  const { t } = useTranslation('createChargerModal');
  const { t: tCommon } = useTranslation('common');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { operatorId: storeOperatorId } = useOperatorStore();
  const operatorId = storeOperatorId || passOperatorId;
  const [fileList, setFileList] = useState([]); // Stores file list with previews
  const [availableDays, setAvailableDays] = useState(
    defaultOpeningTimes.map((d) => d.day)
  );
  const [showHeartbeatModal, setShowHeartbeatModal] = useState(false);
  const [formValuesToSubmit, setFormValuesToSubmit] = useState(null);
  const initializedFormId = useRef(null);

  const { mutateAsync: uploadFile } = useFilUpload(); //

  // 1. Get Charger details
  const { data: chargerDetails, isLoading: isLoadingChargerData } =
    useGetSingleCharger({ id, operatorId }, { enabled: !!id && !!operatorId });

  // Check if charger is online
  const isChargerOnline = chargerDetails?.status === 'ONLINE';

  // Memoize form initial values to prevent unnecessary re-renders
  const formInitialValues = useMemo(() => {
    const baseValues = {
      operatorId,
      locationId,
      isPrivate: false,
      disconnectionThreshold: 60,
    };

    if (chargerDetails) {
      // Editing existing charger
      const formValues = {
        ...baseValues,
        name: chargerDetails.name,
        powerType: chargerDetails.powerType,
        maxCapacity: String(chargerDetails.maxCapacity),
        is24hrOpen: chargerDetails.is24hrOpen,
        openingTimes: chargerDetails.is24hrOpen
          ? []
          : (chargerDetails.openingTimes ?? defaultOpeningTimes).map((item) => ({
            day: item.day,
            timeRange: [
              dayjs(item.begin || item.timeRange?.[0], 'HH:mm'),
              dayjs(item.end || item.timeRange?.[1], 'HH:mm')
            ],
          })),
        connectorDetails: chargerDetails.connectors?.map((connector, index) => ({
          connectorId: String(index + 1), // Always use sequential IDs (1, 2, 3)
          connectorType: connector.connectorType,
          pricePerUnit: connector.pricePerUnit,
        })) || [],
        latitude: chargerDetails.location?.coordinates?.[1],
        longitude: chargerDetails.location?.coordinates?.[0],
        maxSOCPercentage: chargerDetails.maxSOCPercentage ?? '',
        disconnectionThreshold: chargerDetails.disconnectionThreshold ?? 60,
        esuNumber: chargerDetails.esuNumber || '',
      };
      return formValues;
    } else {
      // Creating new charger - default values
      const defaultValues = {
        ...baseValues,
        name: '',
        powerType: 'DC',
        maxCapacity: '60',
        is24hrOpen: true,
        openingTimes: [],
        connectorDetails: [
          { connectorId: '1', connectorType: 'CCS2', pricePerUnit: 0 },
        ],
        latitude: '',
        longitude: '',
        maxSOCPercentage: '',
        esuNumber: '',
      };

      return defaultValues;
    }
  }, [chargerDetails, operatorId, locationId]);

  useEffect(() => {
    if (!isOpen) {
      initializedFormId.current = null;
      return;
    }

    if (id && chargerDetails && initializedFormId.current !== id) {
      form.setFieldsValue(formInitialValues);
      initializedFormId.current = id;
    }
  }, [isOpen, id, chargerDetails, form, formInitialValues]);

  // Debug form values when form is opened and ensure connector IDs are set
  useEffect(() => {
    if (isOpen && !isLoadingChargerData) {
      setTimeout(() => {
        const formValues = form.getFieldsValue();

        // Check if connectorDetails are properly set
        if (formValues.connectorDetails && formValues.connectorDetails.length > 0) {
          const hasEmptyIds = formValues.connectorDetails.some(connector =>
            !connector.connectorId || connector.connectorId === ''
          );

          if (hasEmptyIds) {
            const fixedConnectors = formValues.connectorDetails.map((connector, idx) => ({
              ...connector,
              connectorId: String(idx + 1),
            }));
            form.setFieldsValue({ connectorDetails: fixedConnectors });
          }
        }
      }, 200);
    }
  }, [isOpen, isLoadingChargerData, form]);

  // Handle file list updates when charger details change
  useEffect(() => {
    if (!isOpen || isLoadingChargerData) return;

    if (chargerDetails?.images?.length > 0) {
      setFileList(
        chargerDetails.images.map((url, index) => ({
          uid: String(index),
          name: `image_${index}`,
          status: 'done',
          url,
        }))
      );
    } else {
      setFileList([]);
    }
  }, [chargerDetails, isOpen, isLoadingChargerData]);

  // Debug form values after form is rendered
  useEffect(() => {
    if (!isOpen || isLoadingChargerData) return;

    // Small delay to ensure form is fully rendered
    const timer = setTimeout(() => {
      const currentConnectorDetails = form.getFieldValue('connectorDetails');

      // Check if connectorDetails need to be set
      const needsConnectorData = (
        !currentConnectorDetails ||
        currentConnectorDetails.length === 0 ||
        currentConnectorDetails.every(item => !item.connectorId || !item.connectorType) ||
        currentConnectorDetails.every(item => Object.keys(item).length === 0)
      );

      if (needsConnectorData && chargerDetails?.connectors?.length > 0) {
        const connectorData = chargerDetails.connectors.map((connector, index) => ({
          connectorId: String(index + 1), // Always use sequential IDs (1, 2, 3)
          connectorType: connector.connectorType,
          pricePerUnit: connector.pricePerUnit,
        }));
        form.setFieldValue('connectorDetails', connectorData);

        // Also try setFieldsValue as a fallback
        setTimeout(() => {
          form.setFieldsValue({ connectorDetails: connectorData });
        }, 50);
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [isOpen, isLoadingChargerData, chargerDetails, form]);

  // 2. Setup mutation
  const { mutate: createOrUpdateCharger, isPending: isSaving } =
    useChargerCreateAndUpdate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['GET_SINGLE_CHARGER']);
          queryClient.invalidateQueries({ queryKey: ['chargerList'] });
          queryClient.invalidateQueries({ queryKey: ['listStations'] });
          message.success(
            t(
              id
                ? 'createChargerModal.messages.updateSuccess'
                : 'createChargerModal.messages.createSuccess'
            )
          );
          onOpenChange(false);
        },
        onError: (error) => {
          message.error(
            error?.response?.data?.message ||
            error?.response?.data?.status_message ||
            t('createChargerModal.messages.createFailed')
          );
        },
      }
    );

  const handleFileUpload = async ({ file, onSuccess, onError }) => {
    try {
      setFileList((prevFileList) => [
        ...prevFileList,
        {
          uid: file.uid,
          name: file.name,
          status: 'uploading',
        },
      ]);
      const result = await uploadFile(file);
      setFileList((prevFileList) =>
        prevFileList.map((item) =>
          item.uid === file.uid
            ? { ...item, status: 'done', url: result.Location }
            : item
        )
      );
      onSuccess(result, file);
      message.success(t('createChargerModal.messages.uploadSuccess', { fileName: file.name }));
    } catch (error) {
      setFileList((prevFileList) =>
        prevFileList.map((item) =>
          item.uid === file.uid ? { ...item, status: 'error' } : item
        )
      );
      onError(error);
      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.status_message ||
        t('createChargerModal.messages.uploadFailed')
      );
    }
  };

  // Update available days whenever openingTimes changes
  const updateAvailableDays = useCallback(() => {
    const currentDays = form.getFieldValue('openingTimes') || [];
    const selectedDays = currentDays.map((item) => item.day).filter(Boolean); // Filter out empty values
    const newAvailableDays = defaultOpeningTimes
      .map((d) => d.day)
      .filter((day) => !selectedDays.includes(day));
    setAvailableDays(newAvailableDays);
  }, [form]);

  // Add this effect to update available days when form values change
  useEffect(() => {
    if (form && isOpen) {
      updateAvailableDays();
    }
  }, [updateAvailableDays, form, isOpen]);

  // Update the handleDaySelect function
  const handleDaySelect = () => {
    setTimeout(() => {
      updateAvailableDays();
    }, 0);
  };

  // Add this function to handle modal confirmation
  const handleModalConfirm = () => {
    setShowHeartbeatModal(false);
    if (formValuesToSubmit) {
      createOrUpdateCharger(formValuesToSubmit);
      setFormValuesToSubmit(null);
    }
  };

  // Update handleFinish function
  const handleFinish = (values) => {

    const connectorDetailsPayload = values.connectorDetails.map((c) => {
      // eslint-disable-next-line no-unused-vars
      const { socketNumber, ...rest } = c || {};

      const connectorPayload = {
        connectorId: String(rest.connectorId),
        connectorType: rest.connectorType,
        pricePerUnit: 0,
      };

      return connectorPayload;
    });

    const formattedValues = {
      // if no id, pass operatorId, locationId
      ...(!id && { operatorId }),
      ...(!id && { locationId }),

      name: values.name,
      powerType: values.powerType,
      maxCapacity: Number(values.maxCapacity),
      is24hrOpen: values.is24hrOpen,
      isPrivate: values.isPrivate ?? false,
      disconnectionThreshold: values.disconnectionThreshold,
      // Only include maxSOCPercentage if not null/undefined/empty string
      ...(values.maxSOCPercentage !== undefined && values.maxSOCPercentage !== null && values.maxSOCPercentage !== ''
        ? { maxSOCPercentage: String(`${values.maxSOCPercentage}`) }
        : {}),
      // Include esuNumber if it has a value
      ...(values.esuNumber !== undefined && values.esuNumber !== null && values.esuNumber !== ''
        ? { esuNumber: values.esuNumber }
        : {}),

      // convert timeRange objects to your needed format if not 24hr
      openingTimes: values.is24hrOpen
        ? []
        : values.openingTimes?.map((item) => ({
          day: item.day,
          begin: item.timeRange?.[0]?.format('HH:mm'),
          end: item.timeRange?.[1]?.format('HH:mm'),
        })),

      connectorDetails: connectorDetailsPayload,
      images: fileList.filter(f => f.status === 'done' && f.url).map(f => f.url),
      latitude: String(values.latitude),
      longitude: String(values.longitude),
    };

    // Check if this is an edit and heartbeat value has changed
    if (
      id &&
      chargerDetails?.disconnectionThreshold !== values.disconnectionThreshold
    ) {
      setFormValuesToSubmit(formattedValues);
      setShowHeartbeatModal(true);
    } else {
      createOrUpdateCharger(formattedValues);
    }
  };

  const is24hrOpen = Form.useWatch('is24hrOpen', form) ?? false;
  const openingTimes = Form.useWatch('openingTimes', form) ?? [];

  // Handle 24/7 toggle
  useEffect(() => {
    if (is24hrOpen) {
      // If switching to 24/7, clear opening times
      form.setFieldValue('openingTimes', []);
    } else if (!openingTimes || openingTimes.length === 0) {
      // If switching from 24/7 and no opening times exist, add default
      form.setFieldValue('openingTimes', [
        {
          day: 'Monday',
          timeRange: [dayjs('09:00', 'HH:mm'), dayjs('22:00', 'HH:mm')],
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is24hrOpen, form]);


  return (
    <>
      <Drawer
        maskClosable={false}
        open={isOpen}
        width={isMobile ? '100%' : 800}
        onClose={() => onOpenChange(false)}
        afterOpenChange={(open) => {
          if (open) {
            // Auto-focus the first input when drawer opens
            setTimeout(() => {
              const firstInput = document.querySelector('.ant-drawer .ant-input:not([disabled])');
              if (firstInput) {
                firstInput.focus();
              }
            }, 100);
          }
        }}
        destroyOnHidden
        loading={isLoadingChargerData}
        title={
          id
            ? <Title level={4} className="!mb-0">{t('createChargerModal.title.edit')}</Title>
            : <Title level={4} className="!mb-0">{t('createChargerModal.title.create')}</Title>
        }
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              type="primary"
              loading={isSaving}
              onClick={() => form.submit()}
            >
              {t('createChargerModal.buttons.submit')}
            </Button>
            <Button onClick={() => onOpenChange(false)}>
              {t('createChargerModal.buttons.cancel')}
            </Button>
          </Space>
        }
      >
        <Form
          key={chargerDetails?._id || 'new-charger'}
          form={form}
          layout="vertical"
          initialValues={formInitialValues}
          onFinish={handleFinish}
          // preserve={false} removed - causes issues with Form.List
          scrollToFirstError
        >
          {' '}
          <Form.Item
            label={t('createChargerModal.fields.name')}
            name="name"
            rules={[
              validations.required(tCommon('validation.required')),
              validations.noSpaces(tCommon('validation.no_whitespace')),
              validations.maxLength(30, tCommon('validation.max_30_characters')),
            ]}
          >
            <Input
              placeholder={t('createChargerModal.placeholders.name')}
              autoFocus
              onChange={() => {
                // Name field changed
              }}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('createChargerModal.fields.maxCapacity')}
                name="maxCapacity"
                tooltip={
                  isChargerOnline
                    ? t('createChargerModal.messages.cannotEditWhenOnline')
                    : undefined
                }
                rules={[
                  validations.required(tCommon('validation.required')),
                ]}
              >
                <Select
                  placeholder={t(
                    'createChargerModal.placeholders.maxCapacity'
                  )}
                  disabled={isChargerOnline}
                  onChange={() => {
                    // Max Capacity changed
                  }}
                >
                  <Select.Option value="1.1">1.1 kW</Select.Option>
                  <Select.Option value="3.3">3.3 kW</Select.Option>
                  <Select.Option value="7.4">7.4 kW</Select.Option>
                  <Select.Option value="7.7">7.7 kW</Select.Option>
                  <Select.Option value="10">10 kW</Select.Option>
                  <Select.Option value="15">15 kW</Select.Option>
                  <Select.Option value="22">22 kW</Select.Option>
                  <Select.Option value="30">30 kW</Select.Option>
                  <Select.Option value="60">60 kW</Select.Option>
                  <Select.Option value="80">80 kW</Select.Option>
                  <Select.Option value="100">100 kW</Select.Option>
                  <Select.Option value="120">120 kW</Select.Option>
                  <Select.Option value="160">160 kW</Select.Option>
                  <Select.Option value="180">180 kW</Select.Option>
                  <Select.Option value="240">240 kW</Select.Option>
                  <Select.Option value="360">360 kW</Select.Option>
                  <Select.Option value="400">400 kW</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t('createChargerModal.fields.powerType')}
                name="powerType"
                tooltip={
                  isChargerOnline
                    ? t('createChargerModal.messages.cannotEditWhenOnline')
                    : undefined
                }
                rules={[
                  validations.required(tCommon('validation.required')),
                ]}
              >
                <Select
                  size="middle"
                  options={[
                    {
                      value: 'AC',
                      label: t('createChargerModal.options.ac'),
                    },
                    {
                      value: 'DC',
                      label: t('createChargerModal.options.dc'),
                    },
                  ]}
                  placeholder={t('createChargerModal.placeholders.powerType')}
                  disabled={isChargerOnline}
                  onChange={() => {
                    // Power Type changed
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          {/* Max Capacity */}
          <Divider>{t('createChargerModal.fields.connectorDetails')}</Divider>
          <Form.List name="connectorDetails">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={16} align="bottom">
                      <Col span={4}>
                        <Form.Item
                          {...restField}
                          label={<span className="whitespace-nowrap">{t('createChargerModal.connector.connectorId')}</span>}
                          name={[name, 'connectorId']}
                          tooltip={isChargerOnline ? t('createChargerModal.messages.cannotEditWhenOnline') : undefined}
                          rules={[
                            validations.required(tCommon('validation.required')),
                          ]}
                        >
                          <Input disabled />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          {...restField}
                          label={t('createChargerModal.fields.connectorType')}
                          name={[name, 'connectorType']}
                          tooltip={
                            isChargerOnline
                              ? t('createChargerModal.messages.cannotEditWhenOnline')
                              : undefined
                          }
                          rules={[
                            validations.required(tCommon('validation.required')),
                          ]}
                        >
                          <Select
                            options={[
                              {
                                value: 'TYPE1',
                                label: 'Type1',
                              },
                              {
                                value: 'TYPE2',
                                label: 'Type2',
                              },
                              {
                                value: 'CCS2',
                                label: 'CCS2',
                              },
                              {
                                value: 'GBT',
                                label: 'GBT',
                              },
                              {
                                value: 'LEVDC',
                                label: 'LEVDC',
                              },
                              {
                                value: 'SOCKET',
                                label: 'Socket',
                              },
                            ]}
                            placeholder={t(
                              'createChargerModal.placeholders.connectorType'
                            )}
                            disabled={isChargerOnline}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={3} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '24px' }}>
                        {fields.length > 1 && (
                          <Form.Item style={{ marginBottom: 0 }} tooltip={
                            isChargerOnline
                              ? t('createChargerModal.messages.cannotEditWhenOnline')
                              : ''
                          }>
                            <MinusCircleOutlined
                              style={{
                                color: !isChargerOnline ? 'red' : 'gray',
                                cursor: !isChargerOnline ? 'pointer' : 'not-allowed',
                                fontSize: '16px',
                              }}
                              onClick={() => {
                                if (!isChargerOnline) {
                                  remove(name);
                                  // Re-index remaining connectors to maintain sequential IDs (1, 2, 3)
                                  setTimeout(() => {
                                    const connectors = form.getFieldValue('connectorDetails') || [];
                                    const reIndexed = connectors.map((c, idx) => ({
                                      ...c,
                                      connectorId: String(idx + 1),
                                    }));
                                    form.setFieldsValue({ connectorDetails: reIndexed });
                                  }, 100);
                                }
                              }}
                            />
                          </Form.Item>
                        )}
                      </Col>
                    </Row>
                  ))}
                  {fields.length < MAX_CONNECTORS && (
                    <Tooltip title={
                      isChargerOnline
                        ? t('createChargerModal.messages.cannotEditWhenOnline')
                        : ''
                    }>
                      <Button
                        icon={<PlusOutlined />}
                        type="dashed"
                        disabled={isChargerOnline}
                        onClick={() => {
                          // Use fields.length to get the next sequential ID
                          // const nextId = fields.length + 1;

                          // Add empty connector first
                          add();

                          // Then set the values and ensure proper ordering
                          setTimeout(() => {
                            const currentConnectors = form.getFieldValue('connectorDetails') || [];

                            // Set values for the newly added connector and re-index all connectors
                            if (currentConnectors.length > 0) {
                              const updatedConnectors = currentConnectors.map((connector, idx) => ({
                                ...connector,
                                connectorId: String(idx + 1),
                                connectorType: idx === currentConnectors.length - 1 ? 'CCS2' : (connector.connectorType || 'CCS2'),
                                pricePerUnit: 0,
                              }));

                              form.setFieldsValue({ connectorDetails: updatedConnectors });
                            }
                          }, 100);
                        }}
                      >
                        {t('createChargerModal.buttons.addConnector')}
                      </Button>
                    </Tooltip>
                  )}
                </>
              );
            }}
          </Form.List>
          <Divider>{t('createChargerModal.fields.chargerSettings')}</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="isPrivate"
                valuePropName="checked"
                label={t('createChargerModal.fields.access')}
                initialValue={false}
              >
                <Checkbox>{t('createChargerModal.fields.privateAccess')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('createChargerModal.fields.heartbeatInterval')}
                name="disconnectionThreshold"
                rules={[
                  validations.required(tCommon('validation.required')),
                  {
                    validator: (_, value) => {
                      if (value < 30) {
                        return Promise.reject(
                          tCommon('validation.heartbeat_interval_min')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  type="number"
                  min={30}
                  placeholder="Enter heartbeat interval"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('createChargerModal.fields.maxSOCPercentage')}
                name="maxSOCPercentage"
                tooltip={t('createChargerModal.messages.maxSOCPercentageInfo')}
                rules={[{
                  validator: (_, value) => {
                    if (value === undefined || value === null || value === '') {
                      return Promise.resolve();
                    }
                    if (Number(value) < 11) {
                      return Promise.reject(tCommon('validation.soc_percentage_min'));
                    }
                    if (Number(value) > 100) {
                      return Promise.reject(tCommon('validation.soc_percentage_max'));
                    }
                    return Promise.resolve();
                  },
                }]}
                required={false}
              >
                <Input
                  type="number"
                  min={11}
                  max={100}
                  placeholder={t('createChargerModal.placeholders.maxSOCPercentage')}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>
          <Divider>{t('createChargerModal.fields.timing')}</Divider>
          {/* 24hr Open */}
          <Form.Item
            name="is24hrOpen"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) => {
                  const openingTimes =
                    form.getFieldValue('openingTimes') || [];
                  if (
                    !value &&
                    (!openingTimes || openingTimes.length === 0)
                  ) {
                    return Promise.reject(
                      t('createChargerModal.messages.selectOpeningTime')
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Checkbox>{t('createChargerModal.fields.open24Hours')}</Checkbox>
          </Form.Item>
          {/* Opening Times (shown only if not 24hr) */}
          {!is24hrOpen && (
            <Form.List
              name="openingTimes"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value || value.length === 0) {
                      return Promise.reject(
                        t('createChargerModal.messages.addOpeningTime')
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => {
                    const current = form.getFieldValue(['openingTimes', name, 'day']);
                    const options = [
                      ...(current ? [{ value: current, label: current }] : []),
                      ...defaultOpeningTimes
                        .map(d => d.day)
                        .filter(d => d !== current && !(openingTimes || [])?.some(ot => ot?.day === d))
                        .map(d => ({ value: d, label: d })),
                    ];

                    return (
                      <Row key={key} gutter={16} align="middle">
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            label={t('createChargerModal.fields.day')}
                            name={[name, 'day']}
                            rules={[validations.required(tCommon('validation.required'))]}
                          >
                            <Select
                              options={options}
                              onChange={handleDaySelect}
                              onOpenChange={() => updateAvailableDays()}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            label={t('createChargerModal.fields.timeRange')}
                            name={[name, 'timeRange']}
                            rules={[validations.required(tCommon('validation.required'))]}
                          >
                            <TimePicker.RangePicker
                              format="HH:mm"
                              minuteStep={15}
                              allowClear={false}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '24px' }}>
                          <Button
                            type="link"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => {
                              remove(name);
                            }}
                            disabled={fields.length === 1}
                          />
                        </Col>
                      </Row>
                    );
                  })}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      add({
                        day: '',
                        timeRange: [
                          dayjs('09:00', 'HH:mm'),
                          dayjs('22:00', 'HH:mm'),
                        ],
                      });
                    }}
                    disabled={
                      fields.length >= 7 || availableDays.length === 0
                    } // Disable when all days are used
                  >
                    {t('createChargerModal.buttons.addOpeningTime')}
                  </Button>
                </>
              )}
            </Form.List>
          )}
          <Divider>{t('createChargerModal.fields.mapLocation')}</Divider>
          {/* Latitude/Longitude */}
          <Row gutter={16}>
            {/* Latitude Field */}
            <Col span={12}>
              <Form.Item
                label={t('createChargerModal.fields.latitude')}
                name="latitude"
                rules={[
                  validations.required(tCommon('validation.required')),
                  validations.validNumber(tCommon('validation.invalid_number')),
                  {
                    validator: (_, value) => {
                      const numValue = parseFloat(value);
                      if (numValue < -90 || numValue > 90) {
                        return Promise.reject(
                          new Error(
                            tCommon('validation.latitude_range')
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  validations.noSpaces(
                    tCommon('validation.no_whitespace')
                  ),
                ]}
              >
                <Input
                  placeholder={t('createChargerModal.placeholders.latitude')}
                />
              </Form.Item>
            </Col>

            {/* Longitude Field */}
            <Col span={12}>
              <Form.Item
                label={t('createChargerModal.fields.longitude')}
                name="longitude"
                rules={[
                  validations.required(tCommon('validation.required')),
                  validations.validNumber(tCommon('validation.invalid_number')),
                  {
                    validator: (_, value) => {
                      const numValue = parseFloat(value);
                      if (numValue < -180 || numValue > 180) {
                        return Promise.reject(
                          new Error(
                            tCommon('validation.longitude_range')
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                  validations.noSpaces(
                    tCommon('validation.no_whitespace')
                  ),
                ]}
              >
                <Input
                  placeholder={t('createChargerModal.placeholders.longitude')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label={t('createChargerModal.upload.title')}>
                <Upload
                  listType="picture-card"
                  customRequest={handleFileUpload}
                  fileList={fileList}
                  accept=".png,.jpg,.jpeg"
                  showUploadList
                  onPreview={(file) => {
                    // Handle file preview
                    if (file.url) {
                      window.open(file.url, '_blank');
                    }
                  }}
                  onRemove={(file) =>
                    setFileList((prevFileList) =>
                      prevFileList.filter((item) => item.uid !== file.uid)
                    )
                  }
                >
                  {fileList.length < 8 && (
                    <Button
                      type="text"
                      htmlType='button'
                      className="flex gap-2 flex-col items-center"
                    >
                      <BsPlus size={20} />
                    </Button>
                  )}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>

      <Modal
        title={t('createChargerModal.messages.heartbeatChanged')}
        open={showHeartbeatModal}
        onOk={handleModalConfirm}
        onCancel={() => setShowHeartbeatModal(false)}
        okText={t('createChargerModal.messages.understand')}
        cancelText={t('createChargerModal.messages.cancel')}
      >
        <p>
          {t('createChargerModal.messages.heartbeatMessage')}
        </p>
      </Modal>
    </>
  );
};

// Add prop-types validation
CreateCharger.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  locationId: PropTypes.string,
  passOperatorId: PropTypes.string,
};

export default CreateCharger;
